import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef, useState} from 'react'
import ScrollBar from 'react-perfect-scrollbar'
import {Lang} from 'yougile-lang'
import {DataObjectId, StickerStateId} from 'yougile-model'
import {Sync} from '@client/sync'
import {LinkButtonNew} from '@components/common/base/buttons/link-button-new'
import {CheckboxBase} from '@components/common/base/checkbox-new/checkbox-base'
import {OptionsItem} from '@components/common/base/dropdown-button-menu/types'
import {InputNew} from '@components/common/base/input-new'
import {SelectWithDropdown} from '@components/common/base/select-with-dropdown'
import {Sticker} from '@components/common/base/sticker/sticker'
import {DateRangePicker} from '@components/common/date-picker-old/date-range-picker'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {UsersTable} from '@components/common/users-table/users-table'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {
  StickerFilterDropdownTitle,
  StickerFiltersWrapper,
  TwDateRange,
  TwFilter,
  TwFilterTitle,
  TwStickerFilterDropdown,
  TwStickerFilterValue,
  TwStickerStateRow,
  TwStickersWrappers,
  TwTaskListFilters
} from '@components/my-tasks/tasks-list/containers'
import {useTasksListStore} from '@components/my-tasks/tasks-list/tasks-list-store'
import {
  getStateColor,
  getStatesList
} from '@components/sticker/sticker-menu/sticker-menu-string/utils'
import {globals} from '@extensions/globals-base'
import {SprintCtl} from '@extensions/sticker/controllers/sprint-ctl'
import {Sticker as StickerExtension} from '@extensions/sticker/sticker'

export const TaskListFilters = observer(() => {
  const [state, store] = useTasksListStore()

  return (
    <TwTaskListFilters>
      <TwFilter>
        <TwFilterTitle>{Lang.msg.chat.locationSection}</TwFilterTitle>
        <SelectWithDropdown
          variant="new"
          bgColor="bg-primary"
          options={store.projectFilterOptions}
          value={store.projectFilterValue}
          onChange={store.setProjectFilter}
        />
        {state.filtersValues.project !== '' && (
          <>
            <SelectWithDropdown<DataObjectId | ''>
              variant="new"
              bgColor="bg-primary"
              disabled={!state.filtersValues.project}
              options={store.boardFilterOptions}
              value={store.boardFilterValue}
              onChange={store.setBoardFilter}
            />
            <SelectWithDropdown<DataObjectId | ''>
              variant="new"
              bgColor="bg-primary"
              disabled={!state.filtersValues.board}
              options={store.columnFilterOptions}
              value={store.columnFilterValue}
              onChange={store.setColumnFilter}
            />
          </>
        )}
      </TwFilter>
      <TwFilter>
        <TwFilterTitle>{Lang.msg.deadline}</TwFilterTitle>
        <SelectWithDropdown
          variant="new"
          bgColor="bg-primary"
          value={store.deadlineFilterValue}
          content={
            <TwDateRange>
              <LinkButtonNew onClick={() => store.setDeadlineFilter(undefined)}>
                <div className="text-12">{Lang.msg.boardRemoveFilter}</div>
              </LinkButtonNew>
              <DateRangePicker
                initialDates={state.filtersValues.deadline}
                onChoose={store.setDeadlineFilter}
              />
            </TwDateRange>
          }
        />
      </TwFilter>
      <TwFilter>
        <TwFilterTitle>{Lang.msg.fieldCreatedBy}</TwFilterTitle>
        <SelectWithDropdown
          variant="new"
          bgColor="bg-primary"
          value={store.authorFilterValue}
          content={
            <div className="w-[304px] h-[270px]">
              <UsersTable
                useCheckbox
                className="h-full"
                users={store.authorFilterOptions}
                onSelectedChanged={store.setAuthorFilter}
                selected={state.filtersValues.author || []}
              />
            </div>
          }
        />
      </TwFilter>
      <TwFilter $maxWidth>
        <TwFilterTitle>{Lang.msg.stickers2}</TwFilterTitle>
        <TwStickersWrappers>
          {state.stickerFilterName.map((stickerId, index) => {
            const stickerType = state.stickersOptions.get(stickerId)?.type

            return (
              <StickerFiltersWrapper key={`${stickerId}${index}`}>
                <SelectWithDropdown
                  variant="new"
                  bgColor="bg-primary"
                  options={store.stickerNamesOptions}
                  onChange={(value: OptionsItem<DataObjectId>) => {
                    store.setStickerFilterName(index, value.key)
                  }}
                  value={store.getStickerFilterNameValue(index)}
                />
                {(!stickerType ||
                  ['sprint', 'string'].includes(stickerType)) && (
                  <StatesStickerFilter stickerId={stickerId} />
                )}
                {stickerType && ['text', 'numeric'].includes(stickerType) && (
                  <InputNew
                    type={stickerType === 'numeric' ? 'number' : 'text'}
                    placeholder={Lang.msg.common.enterValue}
                    value={store.getStickerFilterValue(stickerId, stickerType)}
                    onChange={value => {
                      if (stickerId) {
                        store.setStickerFilter(stickerId, value ? [value] : [])
                      }
                    }}
                  />
                )}
              </StickerFiltersWrapper>
            )
          })}
          {state.stickerFilterName?.length < 3 && (
            <LinkButtonNew
              icon="IconCustomPlus"
              onClick={store.addStickerFilterName}
            >
              {Lang.msg.addStickerHint}
            </LinkButtonNew>
          )}
        </TwStickersWrappers>
      </TwFilter>
    </TwTaskListFilters>
  )
})

const StatesStickerFilter = observer(
  ({stickerId}: {stickerId: DataObjectId | ''}) => {
    const [state, store] = useTasksListStore()
    const scrollContainer = useRef<ScrollBar>(null)
    const [menuIsOpened, setMenuIsOpened] = useState<boolean>(false)
    const innerContent = useRef<HTMLDivElement>(null)
    const isDark = globals.theme === 'dark'
    const currentStickerValues =
      (stickerId && state.filtersValues.stickers.get(stickerId)) || []

    const box = Sync.getCurrentSandbox()
    const sticker = stickerId
      ? StickerExtension.getById(box, stickerId)
      : undefined
    const stickerType = sticker && StickerExtension.getType(sticker)
    const allStates = sticker && getStatesList(sticker)
    const stickerIcon = sticker && StickerExtension.getIcon(sticker)
    const isSprintSticker = stickerType === 'sprint'

    const textValue =
      !currentStickerValues?.length || !allStates?.length
        ? Lang.msg.stickerStates2
        : currentStickerValues
            .map(stickerState => {
              const value = allStates.find(
                oneOfAllStates => oneOfAllStates.id === stickerState
              )

              return value?.name || ''
            })
            .join(', ')

    useEffect(() => {
      if (menuIsOpened && scrollContainer.current) {
        scrollContainer.current.updateScroll()
      }
    }, [menuIsOpened])

    const onToggle = () => {
      if (allStates?.length) {
        setMenuIsOpened(prev => !prev)
      }
    }

    const onToggleCheckbox = (stateId: StickerStateId) => {
      if (stickerId) {
        if (currentStickerValues?.includes(stateId)) {
          store.setStickerFilter(
            stickerId,
            currentStickerValues.filter(stateValue => stateValue !== stateId)
          )
        } else {
          store.setStickerFilter(stickerId, [...currentStickerValues, stateId])
        }
      }
    }

    return (
      <Dropdown
        arrow={false}
        reference={innerContent.current}
        visible={menuIsOpened}
        onVisibleChange={onToggle}
        content={
          <TwStickerFilterDropdown>
            {!!sticker?.title && (
              <Tooltip offset={[0, 10]} content={sticker.title}>
                <StickerFilterDropdownTitle>
                  {Lang.msg.sticker} "{sticker.title}"
                </StickerFilterDropdownTitle>
              </Tooltip>
            )}
            <WithPerfectScrollbar
              ref={scrollContainer}
              className={`${
                isDark ? 'perfect-scrollbar-with-custom-scroll' : ''
              }`}
              wheelPropagation
              wheelSpeed={1.5}
            >
              <div className="max-h-250">
                {!!stickerType &&
                  allStates?.map(stickerState => {
                    const stateColor = getStateColor(stickerState, stickerType)

                    return (
                      <TwStickerStateRow
                        key={stickerState.id}
                        onClick={() => onToggleCheckbox(stickerState.id)}
                      >
                        <Sticker
                          disabled
                          icon={isSprintSticker ? '' : stickerIcon}
                          title={
                            <>
                              {stickerState.name}{' '}
                              {isSprintSticker && (
                                <span className="font-semibold">
                                  {SprintCtl.formatInterval(stickerState)}
                                </span>
                              )}
                            </>
                          }
                          color={stateColor}
                          iconColor={
                            isSprintSticker ? undefined : `icon-${stateColor}`
                          }
                          iconBackgroundColor={
                            isSprintSticker ? undefined : `icon-${stateColor}`
                          }
                        />
                        <CheckboxBase
                          value={
                            (stickerId &&
                              state.filtersValues.stickers
                                .get(stickerId)
                                ?.includes(stickerState.id)) ||
                            false
                          }
                          onClick={event => {
                            event.stopPropagation()
                            onToggleCheckbox(stickerState.id)
                          }}
                        />
                      </TwStickerStateRow>
                    )
                  })}
              </div>
            </WithPerfectScrollbar>
          </TwStickerFilterDropdown>
        }
      >
        <div ref={innerContent}>
          <Tooltip
            disabled={!currentStickerValues?.length}
            offset={[0, 10]}
            content={textValue}
          >
            <TwStickerFilterValue
              $active={menuIsOpened}
              $disabled={!allStates?.length}
              onClick={onToggle}
            >
              <div className="min-w-0 truncate">{textValue}</div>
            </TwStickerFilterValue>
          </Tooltip>
        </div>
      </Dropdown>
    )
  }
)
